import { useCashOutSheets } from '@/components/CashOutSheet';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { CashableEntry, cashOutPollingKey, usePerformCashoutEntry } from '@/feature/entries-pickem/hooks/use-cash-out';
import { CashOutSource, withCashOutAnalytics } from '@/hooks/cash-out-analytics';
import type { CashOutResult, EntryCashOut } from '@/hooks/use-entry-cash-out';
import { useEntryCashOut } from '@/hooks/use-entry-cash-out';
import { useFormatCurrency } from '@/hooks/use-format-currency';
import { Status } from '@/types/api.generated';
import { CashoutResponse, ProblemDetail } from '@/types/cashout.generated';
import { logger } from '@/utils/logging';
import { useQueryClient } from '@tanstack/react-query';

import type { PickemCashedOutEntry } from './use-pickem-cashed-out-entries';
import { usePickemCashedOutEntries } from './use-pickem-cashed-out-entries';

const LOG_TAG = 'PickemEntryCashOut';

const isProblemDetailType = (error: unknown): error is ProblemDetail =>
    typeof error === 'object' && error !== null && 'status' in error && 'updatedCashoutAmount' in error;

export const usePickemEntryCashOut = (
    item: CashableEntry,
    cashoutOffer: number | null,
    source: CashOutSource
): EntryCashOut => {
    const { formatCurrency } = useFormatCurrency();
    const { showPriceChange, showCashOutError } = useCashOutSheets();
    const queryClient = useQueryClient();
    const { mutateAsync: cashoutEntry } = usePerformCashoutEntry();

    const cachedCashedOut = usePickemCashedOutEntries(s => s.entries.get(item.id)) ?? null;
    const markCashedOut = usePickemCashedOutEntries(s => s.markCashedOut);

    const canCashOut = item?.canBeCashedout ?? false;

    // The cached cashed-out entry is only relevant if the entry hasn't been settled yet. Once the entry settled and status updated, we need to rely on the data from BE.
    const effectiveCachedCashedOut = item.status !== Status.CashedOut ? cachedCashedOut : null;

    const confirmCashOut = async (amount: number): Promise<CashOutResult<PickemCashedOutEntry>> => {
        try {
            await cashoutEntry({ entryId: item.id, amount });
        } catch (error) {
            if (isProblemDetailType(error) && error.status === 409 && error.updatedCashoutAmount) {
                return { type: 'offerChange', amount: error.updatedCashoutAmount };
            }
            throw error;
        }
        return { type: 'success', entry: { id: item.id, cashedOutAmount: amount } };
    };

    const onError = (error: unknown, retry: () => void) => {
        logger.warn(LOG_TAG, `Inline cash-out failed for entry ${item.id}`, error);
        BetrAnalytics.trackProductEvent(AnalyticsEvent.CASH_OUT_ERROR);
        showCashOutError({ onRetry: retry });
    };

    const onOfferChange = ({ previous, next, complete }: { previous: number; next: number; complete: () => void }) => {
        // Write the re-price into the poll so the idle CTA reads it before the refetch lands.
        queryClient.setQueryData<CashoutResponse>(
            cashOutPollingKey(item.id),
            old => old && { ...old, cashoutAmount: next }
        );
        queryClient.invalidateQueries({ queryKey: cashOutPollingKey(item.id) });
        BetrAnalytics.trackProductEvent(AnalyticsEvent.CASH_OUT_PRICE_CHANGED, { previous, next });
        showPriceChange({ previous, next, onComplete: complete });
    };

    const cashOut = useEntryCashOut<PickemCashedOutEntry>({
        canCashOut,
        offer: cashoutOffer,
        cashedOutAmount: entry => entry?.cashedOutAmount ?? 0,
        confirmCashOut,
        cachedCashedOut: effectiveCachedCashedOut,
        markCashedOut: entry => {
            BetrAnalytics.trackProductEvent(AnalyticsEvent.CASH_OUT_SUCCESS, { amount: entry.cashedOutAmount });
            return item.id && markCashedOut(item.id, entry);
        },
        onError,
        onOfferChange,
        formatCurrency,
        labels: {
            controls: {
                offer: 'Cash out',
                confirm: 'Confirm cash out',
                cancel: 'Cancel',
                confirmed: 'Cashed out confirmed',
            },
            payoutColumn: { payout: 'Payout', cash_out: 'Cash out' },
        },
    });

    return withCashOutAnalytics(cashOut, source);
};
